import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Backdrop, Box, Divider, LinearProgress, Stack, Typography } from '@mui/material';
import { Loading } from 'components/styleguide';
import { useRootTranslation } from 'utils/hooks';
import { useSearchTracksPartnersQuery } from 'services/tracks/tracks';
import { openPrompt } from 'redux-core/prompt/actions';
import {
  resolveImportRowsCall,
  deleteTrackImportAliasCall,
} from 'redux-core/import-tracks-spreadsheet/resolverServices';
import { processImportFileCall } from 'redux-core/import-tracks-spreadsheet/services';
import { setResolverFinishingOnDialog } from 'redux-core/import-tracks-spreadsheet/actions';
import {
  createUnmatchedSource,
  consumeUnmatchedPrefetch,
  MatchProgress,
} from 'redux-core/import-tracks-spreadsheet/unmatchedPrefetch';
import { processingPrompt } from '../ProcessingPrompt';
import { RESOLVER_ACTIONS, RESOLVER_GRID } from './constants';
import {
  resolverReducer,
  initialState,
  deriveMatchedCount,
  buildResolvePayload,
  ApiResolverRow,
  ResolverOption,
  StoredAlias,
} from './reducer';
import ResolverHeader from './ResolverHeader';
import ResolverRow from './ResolverRow';
import { RowHandlers } from './ResolverRow/QwireTracksFields';

const rootT = 'drawers.tracksImportResolver';
const drawerRootT = 'drawers.importTracksSpreadsheetDrawer';

type ResolverStepProps = {
  jobId: string;
  divisionId: number;
  onClose: () => void;
};

/* The drawer's "is it stuck?" answer. Matching used to run for minutes behind a bare
   spinner (see the Pass-B query fix, 2026-08-10) and users reasonably concluded it had
   hung. A determinate bar with real row counts is the honest version: it moves per row
   RESOLVED, so it keeps advancing through a file where nothing matches.
   Three states, deliberately distinct:
     no progress yet / legacy job -> indeterminate spinner (never a fake 0%)
     READING_FILE (total 0)       -> indeterminate, "reading your spreadsheet"
     MATCHING                     -> determinate "34 of 100" + percentage */
const MatchingIndicator = ({
  progress,
  t,
}: {
  progress: MatchProgress | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) => {
  const determinate = Boolean(progress) && progress.total > 0;
  const percent = determinate ? Math.min(100, Math.round((progress.processed / progress.total) * 100)) : 0;

  if (!determinate) {
    return (
      <Stack gap={2} alignItems="center" data-testid="resolver-matching-indeterminate">
        <Loading />
        <Typography variant="body2">
          {progress?.phase === 'READING_FILE' ? t('readingFile') : t('stillMatching')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={1.5} alignItems="center" sx={{ width: 360, maxWidth: '100%' }} data-testid="resolver-matching-progress">
      <Typography variant="body2">{t('stillMatching')}</Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={t('stillMatching')}
        sx={{ width: '100%', height: 6, borderRadius: 3 }}
      />
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {t('matchingProgress', { processed: progress.processed, total: progress.total, percent })}
      </Typography>
    </Stack>
  );
};

const ResolverStep = ({ jobId, divisionId, onClose }: ResolverStepProps) => {
  const t = useRootTranslation(rootT);
  const tDrawer = useRootTranslation(drawerRootT);
  const dispatch = useDispatch();
  const [state, dispatchResolver] = useReducer(resolverReducer, initialState);
  const [isFinishing, setIsFinishing] = useState(false);
  const [matchTimedOut, setMatchTimedOut] = useState(false);
  /* Live Phase-1 progress, pushed by the poll below. null until the first response
     carries one (and on legacy jobs, which never report it) — the loading state falls
     back to its indeterminate spinner rather than rendering a misleading 0%. */
  const [matchProgress, setMatchProgress] = useState<MatchProgress | null>(null);
  /* Stakeholder review #1: "view ALL tracks versus Unmatched tracks". OFF = the QW-129
     default (only rows needing a decision); ON = engine-matched rows interleave in
     spreadsheet order as read-only reference. Display-only — auto-finish for fully-
     matched imports is untouched (the grid never appears for those). */
  const [showAllTracks, setShowAllTracks] = useState(false);
  const partnersQuery = useSearchTracksPartnersQuery({ term: '', limit: 0 });
  const partnersSettled = partnersQuery.isSuccess || partnersQuery.isError;
  const partnersContent = partnersQuery.data?.content;
  /* searchTracksPartners runs with keepUnusedDataFor: 0, so partnersContent gets a fresh
     array identity on every refetch/subscription change even when isSuccess/isError never
     visibly change. Read through a ref (kept fresh here, independent of the poll effect
     below) instead of depending on partnersContent directly — otherwise every churn would
     re-run the poll effect, cancel the in-flight poll, and restart its 5-minute budget. */
  const partnersContentRef = useRef(partnersContent);
  useEffect(() => {
    partnersContentRef.current = partnersContent;
  }, [partnersContent]);

  useEffect(() => {
    if (!partnersSettled) return undefined;
    let cancelled = false;

    /* The prefetch usually started while the user was still on the upload step, so its
       source already carries whatever progress the match has made — seed from it before
       subscribing so the bar doesn't sit empty for one poll interval. */
    const source = consumeUnmatchedPrefetch(jobId) ?? createUnmatchedSource(jobId);
    setMatchProgress(source.getProgress());
    const unsubscribeProgress = source.subscribeProgress((progress) => {
      if (!cancelled) setMatchProgress(progress);
    });

    const loadRows = async () => {
      try {
        const { rows, divisionAliases, timedOut } = await source.promise;
        if (cancelled) return;
        if (timedOut) {
          /* Keep state.loaded false so FINISH IMPORT stays disabled — the match is still
             running server-side; finishing against an empty grid would import whatever
             it eventually settles on, sight unseen. */
          setMatchTimedOut(true);
          return;
        }
        const partners = (partnersContentRef.current ?? []).map(({ id, name }) => ({ id, name }));
        dispatchResolver({
          type: RESOLVER_ACTIONS.ROWS_LOADED,
          apiRows: rows as ApiResolverRow[],
          partners,
          divisionAliases: divisionAliases as StoredAlias[],
        });
      } catch (error) {
        if (!cancelled) {
          dispatchResolver({ type: RESOLVER_ACTIONS.ROWS_LOADED, apiRows: [], partners: [], divisionAliases: [] });
        }
      }
    };
    loadRows();

    return () => {
      cancelled = true;
      unsubscribeProgress();
      source.cancel();
    };
    // partnersContent is intentionally excluded — see partnersContentRef above; this effect
    // must run exactly once per (jobId, partnersSettled-becoming-true), not on every
    // partners cache churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, partnersSettled]);

  const matched = deriveMatchedCount(state.rows, state.divisionAliases);

  /* QW-129: engine-matched rows are not the user's to touch (see ResolverRow in
     reducer.ts), so they don't render — the grid lists only rows needing a decision.
     The header counter keeps counting ALL rows for the whole-file picture. */
  const unmatchedRows = state.rows.filter((row) => !row.engineMatched);
  /* Libraries typed as "create new" exist only at FINISH IMPORT — the partners search
     can't return them, so later rows couldn't pick the same new library without retyping
     it (stakeholder review #1). Deduped case-insensitively, first spelling wins — the BE
     get-or-creates the partner case-insensitively anyway. */
  const pendingNewLibraries = useMemo(() => {
    const seen = new Map<string, string>();
    state.rows.forEach(({ resolution }) => {
      const name = resolution.newLibraryName?.trim();
      if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
    });
    return Array.from(seen.values());
  }, [state.rows]);
  /* rows.length > 0 keeps the load-error path (catch above lands loaded with zero rows)
     out of auto-finish — never import off a response the user hasn't seen. */
  const allEngineMatched = state.loaded && state.rows.length > 0 && unmatchedRows.length === 0;

  const handlers: RowHandlers = useMemo(
    () => ({
      onLibrarySelected: (itemId: string, library: ResolverOption) =>
        dispatchResolver({ type: RESOLVER_ACTIONS.LIBRARY_SELECTED, itemId, library }),
      onLibraryCreated: (itemId: string, name: string) =>
        dispatchResolver({ type: RESOLVER_ACTIONS.LIBRARY_CREATED, itemId, name }),
      onTrackSelected: (itemId: string, track: ResolverOption) =>
        dispatchResolver({ type: RESOLVER_ACTIONS.TRACK_SELECTED, itemId, track }),
      onTrackCreated: (itemId: string, title: string) =>
        dispatchResolver({ type: RESOLVER_ACTIONS.TRACK_CREATED, itemId, title }),
      onUseChanged: (itemId: string, use: string | null) =>
        dispatchResolver({ type: RESOLVER_ACTIONS.USE_CHANGED, itemId, use }),
      onDurationChanged: (itemId: string, duration: string | null) =>
        dispatchResolver({ type: RESOLVER_ACTIONS.DURATION_CHANGED, itemId, duration }),
      onRowCleared: (itemId: string) => dispatchResolver({ type: RESOLVER_ACTIONS.ROW_CLEARED, itemId }),
      onAliasRemoved: (itemId: string) => dispatchResolver({ type: RESOLVER_ACTIONS.ALIAS_REMOVED, itemId }),
    }),
    [],
  );

  const handleStoredAliasDeleted = (alias: StoredAlias) => {
    dispatch(
      openPrompt({
        content: t('deleteAliasConfirm', { vendorTitle: alias.vendorTitle }),
        onConfirm: async () => {
          await deleteTrackImportAliasCall({ id: alias.id });
          dispatchResolver({ type: RESOLVER_ACTIONS.DIVISION_ALIAS_DELETED, aliasId: alias.id });
        },
      }),
    );
  };

  const handleFinish = useCallback(async () => {
    /* Order matters (QW-102): resolve records + applies the decisions while the job is
       still paused; /process then claims it (AWAITING_RESOLUTION → IMPORTING) and runs
       Phase 2. Reversed, the claim would refuse the late resolutions. */
    setIsFinishing(true);
    dispatch(setResolverFinishingOnDialog(true));
    try {
      const { resolutions } = buildResolvePayload(state.rows, state.divisionAliases);
      /* A fully-matched import has nothing to record (engine-matched rows never travel,
         see buildResolvePayload) — /process claims the paused job directly. */
      if (resolutions.length > 0) {
        await resolveImportRowsCall({ jobId, resolutions });
      }
      processImportFileCall({ jobId, divisionId });
      await dispatch(openPrompt(processingPrompt(tDrawer)));
      onClose();
    } finally {
      setIsFinishing(false);
      dispatch(setResolverFinishingOnDialog(false));
    }
  }, [state.rows, state.divisionAliases, jobId, divisionId, dispatch, tDrawer, onClose]);

  const autoFinishFired = useRef(false);
  useEffect(() => {
    /* QW-129: every row engine-matched — nothing to resolve, so finish the import
       without ever showing the grid. Ref-guarded to fire exactly once (handleFinish's
       identity changes as the finish flips isFinishing). */
    if (!allEngineMatched || autoFinishFired.current) return;
    autoFinishFired.current = true;
    handleFinish();
  }, [allEngineMatched, handleFinish]);

  if (allEngineMatched) {
    /* Auto-finish in flight — the resolve screen never appears for a fully-matched
       import; the processing prompt (opened by handleFinish) is the user feedback. */
    return (
      <Box
        sx={{ display: 'grid', placeContent: 'center', minHeight: 'calc(100vh - 160px)' }}
        data-testid="resolver-auto-finishing"
      >
        <Stack gap={2} alignItems="center">
          <Loading />
          <Typography variant="body2">{t('allMatchedFinishing')}</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack gap={3} sx={{ width: '100%', pb: 6 }}>
      {/* Double-submit guard while /resolve records the decisions (~1-2s; /process is
          fire-and-forget — the "processing" prompt + summary email cover the rest).
          The overlay must cover the drawer's visible viewport at any scroll position,
          and the MuiDrawer-paper is the SCROLLING element — an absolute child would
          scroll away with the content. So: a zero-height sticky anchor rides the
          scroll (height:0 = no layout shift), and the Backdrop bleeds one padding
          upward (top:-24, height:100vh+24px) because sticky can't lift the anchor
          above its flow position under the Paper's top padding at scrollTop < 24.
          The -24/-32 offsets mirror BaseDrawer's padding (theme.spacing(3)/(4),
          see components/styleguide/drawers/BaseDrawer/styles.js). */}
      <Box sx={{ position: 'sticky', top: -24, height: 0, zIndex: 10 }}>
        <Backdrop
          open={isFinishing}
          unmountOnExit
          data-testid="resolver-finishing-backdrop"
          sx={{
            position: 'absolute',
            top: -24,
            left: -32,
            right: -32,
            height: 'calc(100vh + 24px)',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <Loading />
        </Backdrop>
      </Box>
      <ResolverHeader
        matched={matched}
        total={state.rows.length}
        onCancel={onClose}
        onFinish={handleFinish}
        finishDisabled={!state.loaded}
        matching={!state.loaded}
        showAllTracks={showAllTracks}
        onShowAllTracksChange={setShowAllTracks}
      />
      {state.loaded ? (
        <>
          <Box sx={{ display: 'grid', ...RESOLVER_GRID }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('columns.imported')}
            </Typography>
            {/* consumes the gutter track so the remaining headers land in their columns */}
            <Box aria-hidden="true" />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('columns.qwireTracks')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('columns.mappedAliases')}
            </Typography>
          </Box>
          <Stack gap={1.5}>
            {(showAllTracks ? state.rows : unmatchedRows).map((row) => (
              <Fragment key={row.itemId}>
                <ResolverRow
                  row={row}
                  handlers={handlers}
                  divisionAliases={state.divisionAliases}
                  onStoredAliasDeleted={handleStoredAliasDeleted}
                  pendingNewLibraries={pendingNewLibraries}
                />
                <Divider />
              </Fragment>
            ))}
          </Stack>
        </>
      ) : (
        <Box sx={{ display: 'grid', placeContent: 'center', minHeight: 'calc(100vh - 160px)' }}>
          {matchTimedOut ? (
            <Stack
              gap={1}
              alignItems="center"
              sx={{ textAlign: 'center', maxWidth: 480 }}
              data-testid="resolver-match-timeout"
            >
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {t('matchTimeoutTitle')}
              </Typography>
              <Typography variant="body2">{t('matchTimeoutBody')}</Typography>
            </Stack>
          ) : (
            <MatchingIndicator progress={matchProgress} t={t} />
          )}
        </Box>
      )}
    </Stack>
  );
};

export default ResolverStep;
